import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useColumnDrag } from "@/hooks/use-column-drag";
import { useClosedOpportunities, useUpdateOpportunityStage } from "@/hooks/use-opportunities";
import { usePipeBoard } from "@/hooks/use-pipe-board";
import { usePipeFilter } from "@/hooks/use-pipe-filter";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { MOVABLE_STAGES } from "@/lib/opportunity-calc";
import { PIPE_STAGES, type PipeStage } from "@/lib/pipe-board-calc";
import { pipeOwnerId } from "@/lib/pipe-filter";
import { Skeleton } from "@/components/ui/skeleton";
import ClosedOpportunitiesTable from "./ClosedOpportunitiesTable";
import OpportunityDrawer from "./OpportunityDrawer";
import PipeColumn from "./PipeColumn";
import PipeFilterBar from "./PipeFilterBar";
import PipeSummaryCards from "./PipeSummaryCards";

/** Pipe: Outreach, Møte og Tilbud som kolonner, med filter, vektet verdi og avsluttede. */
export default function PipeTab() {
  const { activeUser } = useImpersonation();
  const [filter, updateFilter] = usePipeFilter();
  const [showClosed, setShowClosed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const updateStage = useUpdateOpportunityStage();

  const ownerId = pipeOwnerId(filter, activeUser?.id);
  const source = filter.source === "all" ? undefined : filter.source;
  // «Mine» uten lastet bruker ville ellers hentet og vist alles prosesser.
  const ready = filter.person !== "mine" || !!activeUser;
  const { board, isLoading } = usePipeBoard({ ownerId, source, enabled: ready });
  const { data: closed = [], isLoading: closedLoading } = useClosedOpportunities({ ownerId, source, enabled: showClosed && ready });

  const byId = useMemo(() => {
    const map = new Map(PIPE_STAGES.flatMap((s) => board.columns[s].map((c) => [c.opportunity.id, c.opportunity] as const)));
    for (const o of closed) map.set(o.id, o);
    return map;
  }, [board, closed]);

  const handleDrop = useCallback(
    (cardId: string, column: string) => {
      const opportunity = byId.get(cardId);
      if (!opportunity || opportunity.stage === column) return;
      if (column === "offer") {
        toast.info("Lag et tilbud fra prosessen for å flytte den hit");
        return;
      }
      if (!MOVABLE_STAGES.includes(opportunity.stage)) {
        toast.info("Prosesser i Tilbud følger tilbudet og kan ikke flyttes hit");
        return;
      }
      updateStage.mutate({ id: cardId, stage: column as PipeStage });
    },
    [byId, updateStage],
  );
  const drag = useColumnDrag(handleDrop);

  const counts = {
    outreach: board.columns.outreach.length,
    meeting: board.columns.meeting.length,
    offer: board.columns.offer.length,
  };

  return (
    <div className="space-y-6">
      <PipeFilterBar filter={filter} onChange={updateFilter} showClosed={showClosed} onToggleClosed={() => setShowClosed((v) => !v)} />
      {ready && <PipeSummaryCards weighted={board.weighted} counts={counts} />}

      {isLoading || !ready ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {PIPE_STAGES.map((stage) => (
            <PipeColumn key={stage} stage={stage} cards={board.columns[stage]} drag={drag} onOpen={setSelectedId} />
          ))}
        </div>
      )}

      {showClosed && ready && <ClosedOpportunitiesTable rows={closed} isLoading={closedLoading} onOpen={setSelectedId} />}

      <OpportunityDrawer opportunity={selectedId ? (byId.get(selectedId) ?? null) : null} onClose={() => setSelectedId(null)} />
    </div>
  );
}
